import React, { useMemo, useState, useEffect } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { showListSkeleton, useFirstLoad } from '@/lib/loadingState';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { shopifyApi, formatCurrency } from '@/lib/api/services/shopify';
import { toast } from '@/hooks/use-toast';
import { Search, X, Loader2, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

interface ShopifyCheckoutsProps {
    selectedAccountId: string | null;
    isConnected: boolean;
    shopCurrency?: string;
    refreshKey?: number;
}

export function ShopifyCheckouts({ selectedAccountId, isConnected, shopCurrency = 'USD', refreshKey = 0 }: ShopifyCheckoutsProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [localCheckouts, setLocalCheckouts] = useState<any[]>([]);
    const [totalCheckouts, setTotalCheckouts] = useState(0);
    const [loading, setLoading] = useState(false);
    const { hasLoaded, markLoaded } = useFirstLoad();
    const [error, setError] = useState<string | null>(null);

    const getParam = (key: string, def: string) => searchParams.get(key) || def;
    const setParam = (key: string, value: string | null) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (value === null) newParams.delete(key);
            else newParams.set(key, value);
            return newParams;
        });
    };

    const checkoutsSearch = getParam('checkouts_search', '');
    const checkoutsPage = parseInt(getParam('checkouts_page', '1'));
    const checkoutsPageSizeStr = getParam('checkouts_page_size', '10');
    const checkoutsPageSize = parseInt(checkoutsPageSizeStr, 10);
    const checkoutsStatus = searchParams.get('checkouts_status') === 'needs_message' ? 'needs_message' : undefined;

    const [searchInput, setSearchInput] = useState(checkoutsSearch);

    useEffect(() => {
        setSearchInput(prev => (prev === checkoutsSearch ? prev : checkoutsSearch));
    }, [checkoutsSearch]);

    useEffect(() => {
        if (searchInput === checkoutsSearch) return;
        const t = setTimeout(() => {
            setSearchParams(prev => {
                const p = new URLSearchParams(prev);
                if (searchInput) p.set('checkouts_search', searchInput);
                else p.delete('checkouts_search');
                p.set('checkouts_page', '1');
                return p;
            });
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput, checkoutsSearch, setSearchParams]);

    useEffect(() => {
        const targetId = selectedAccountId;
        if (!isConnected || !targetId) return;
        const offset = (checkoutsPage - 1) * checkoutsPageSize;
        setLoading(true);
        setError(null);
        shopifyApi.getLocalCheckouts(targetId, checkoutsPageSize, offset, { status: checkoutsStatus })
            .then((res) => {
                setLocalCheckouts(Array.isArray(res.checkouts) ? res.checkouts : []);
                setTotalCheckouts(res.total ?? 0);
            })
            .catch((e: any) => setError(friendlyMessage(e, "We couldn't load abandoned checkouts. Please try again.")))
            .finally(() => { setLoading(false); markLoaded(); });
    }, [isConnected, selectedAccountId, refreshKey, checkoutsPage, checkoutsPageSize, checkoutsStatus]);

    const filteredCheckouts = useMemo(() => {
        let result = [...localCheckouts];

        if (checkoutsSearch) {
            const searchLower = checkoutsSearch.toLowerCase();
            result = result.filter(c =>
                c.shopify_checkout_id?.toLowerCase().includes(searchLower) ||
                c.email?.toLowerCase().includes(searchLower) ||
                c.phone?.includes(searchLower) ||
                c.customer_name?.toLowerCase().includes(searchLower)
            );
        }

        return result;
    }, [localCheckouts, checkoutsSearch]);

    const checkoutsColumns: Column<typeof filteredCheckouts[0]>[] = useMemo(() => [
        {
            key: 'checkout',
            header: 'Checkout ID',
            render: (_, checkout) => (
                <span className="font-medium text-xs sm:text-sm">
                    #{checkout.shopify_checkout_id?.slice(-8) || '—'}
                </span>
            ),
            className: 'font-medium text-xs sm:text-sm',
        },
        {
            key: 'date',
            header: 'Date',
            render: (_, checkout) => (
                <div className="flex flex-col">
                    <span className="text-xs sm:text-sm">
                        {checkout.updated_at ? new Date(checkout.updated_at).toLocaleDateString() : '—'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                        {checkout.updated_at ? new Date(checkout.updated_at).toLocaleTimeString() : ''}
                    </span>
                </div>
            ),
            className: 'text-xs sm:text-sm',
        },
        {
            key: 'customer',
            header: 'Customer',
            render: (_, checkout) => (
                <div className="space-y-1">
                    {checkout.customer_name && (
                        <div className="text-xs sm:text-sm font-medium">{checkout.customer_name}</div>
                    )}
                    {checkout.email && (
                        <div className="text-xs sm:text-sm text-muted-foreground">{checkout.email}</div>
                    )}
                    {checkout.phone && (
                        <div className="text-xs text-muted-foreground">{checkout.phone}</div>
                    )}
                    {!checkout.email && !checkout.phone && !checkout.customer_name && (
                        <span className="text-xs sm:text-sm text-muted-foreground">—</span>
                    )}
                </div>
            ),
            className: 'text-xs sm:text-sm',
        },
        {
            key: 'items',
            header: 'Items',
            render: (_, checkout) => {
                let lineItems = checkout.line_items;
                if (typeof lineItems === 'string') {
                    try { lineItems = JSON.parse(lineItems); } catch (e) { lineItems = []; }
                }
                if (!Array.isArray(lineItems)) lineItems = [];
                return (
                    <div className="text-xs sm:text-sm">
                        {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
                    </div>
                )
            },
            className: 'text-xs sm:text-sm',
        },
        {
            key: 'total',
            header: 'Total',
            render: (_, checkout) => (
                <span className="text-xs sm:text-sm font-medium">
                    {formatCurrency(parseFloat(checkout.total_price || '0'), checkout.currency || 'USD')}
                </span>
            ),
            className: 'text-xs sm:text-sm',
        },
        {
            key: 'status',
            header: 'Recovery Status',
            render: (_, checkout) => {
                const isRecovered = checkout.recovery_status === 'recovered';
                return (
                    <Badge variant={isRecovered ? 'default' : 'secondary'} className="text-xs w-fit capitalize">
                        {checkout.recovery_status || 'abandoned'}
                    </Badge>
                );
            },
            className: 'text-xs sm:text-sm',
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (_, checkout) => {
                const isRecovered = checkout.recovery_status === 'recovered';
                if (isRecovered || !checkout.abandoned_checkout_url) return null;
                return (
                    <a href={checkout.abandoned_checkout_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                        Recovery Link
                    </a>
                )
            },
            className: 'text-xs sm:text-sm',
        }
    ], []);

    if (!isConnected) {
        return (
            <div className="text-center py-6 sm:py-8 space-y-3 sm:space-y-4">
                <p className="text-xs sm:text-sm text-muted-foreground">Please connect your Shopify shop first to view abandoned checkouts.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {error && <div className="text-xs sm:text-sm text-destructive p-3 bg-destructive/10 rounded">{error}</div>}

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-center justify-between">
                <div className="flex flex-1 gap-2 items-center w-full sm:w-auto">
                    <div className="relative flex-1 sm:max-w-[300px]">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search checkouts..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="pl-9 h-9"
                        />
                        {searchInput && (
                            <X className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground cursor-pointer" onClick={() => setSearchInput('')} />
                        )}
                    </div>
                    {checkoutsStatus === 'needs_message' && (
                        <Badge variant="secondary" className="h-9 px-3 text-xs gap-1.5 shrink-0">
                            Never messaged
                            <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => setParam('checkouts_status', null)}
                            />
                        </Badge>
                    )}
                </div>
            </div>

            <div className="rounded-md border">
                <DataTable
                    columns={checkoutsColumns}
                    data={filteredCheckouts}
                    currentPage={checkoutsPage}
                    pageSize={checkoutsPageSize}
                    totalItems={totalCheckouts}
                    onPageChange={(p) => setParam('checkouts_page', p.toString())}
                    onPageSizeChange={(s) => {
                        setSearchParams(prev => {
                            const newParams = new URLSearchParams(prev);
                            newParams.set('checkouts_page_size', s.toString());
                            newParams.set('checkouts_page', '1');
                            return newParams;
                        });
                    }}
                    loading={showListSkeleton({ loading, hasLoaded, willFetch: isConnected && !!selectedAccountId })}
                    emptyMessage="No abandoned checkouts found."
                    showPagination={true}
                />
            </div>
        </div>
    );
}
